import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { navigate } from 'vike/client/router';

import { useIsClient } from '@/renderer/utils/hooks/useIsClient';
import { isNativePlatform } from '@/renderer/utils/isNativePlatform';

/**
 * Top-left "Back" affordance shown only inside the native app (the web has
 * browser chrome for this). Goes back in history, or to the landing if there's
 * nowhere to go back to.
 *
 * Rendered client-side only (useIsClient) so the prerendered markup — which is
 * shared by web and the app bundle — never ships a stray Back button to the web.
 */
export default function AppBackButton() {
  const isClient = useIsClient();
  const visualTop = useVisualViewportTop();

  if (!isClient || !isNativePlatform()) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label="Back"
      onClick={() => {
        if (typeof window !== 'undefined' && window.history.length > 1) {
          history.back();
        } else {
          navigate('/');
        }
      }}
      // translateY re-pins the button to what the user actually sees; see
      // useVisualViewportTop.
      style={{ transform: `translateY(${visualTop}px)` }}
      // The top offset STACKS on the status-bar inset (`calc`, master's idiom)
      // rather than `max()`-ing against it: the native shell paints edge-to-edge,
      // so a `max()` that loses to a 1rem floor puts the button on the clock.
      className="fixed top-[calc(0.5rem+var(--safe-top))] left-2 z-50 inline-flex items-center gap-0.5 rounded-lg bg-white/80 px-2 py-1.5 font-medium text-sm text-zinc-600 backdrop-blur transition-colors hover:bg-zinc-500/10 dark:bg-zinc-950/80 dark:text-zinc-300"
    >
      <ChevronLeftIcon className="size-5" />
      Back
    </button>
  );
}

/**
 * How far the visual viewport has been pushed down the layout viewport, in px.
 *
 * The auth screens autofocus their first field, so on iOS the keyboard is up the
 * moment you arrive. The shell does not resize the WebView for it — WebKit
 * scrolls the VISUAL viewport instead, and `position: fixed` resolves against
 * the LAYOUT viewport, so a "pinned" button rides up the screen with the content
 * and lands on the status-bar clock. Adding this offset back puts it where fixed
 * was supposed to keep it.
 *
 * Always 0 where visualViewport is absent or unshifted (web, Android, keyboard
 * closed), so the transform is a no-op there.
 */
function useVisualViewportTop(): number {
  const [top, setTop] = useState(0);

  useEffect(() => {
    const vv = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!vv) {
      return;
    }
    const sync = () => setTop(vv.offsetTop);
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, []);

  return top;
}
